import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Plus, Pencil, Trash2, Copy, CheckCircle2, Music2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHANNELS = ["TikTok", "Facebook", "Instagram", "Google", "Website", "QR Code", "Referral", "Upfirst AI", "GoHighLevel", "Other"];
const EMPTY = { name: "", channel: "TikTok", kind: "campaign", video_url: "", cost: "", views: "", reach: "", notes: "" };

const fmt$ = (v) => `$${Number(v || 0).toLocaleString()}`;

export default function CampaignManager({ token, kindFilter, title }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState("");
  const H = { headers: { Authorization: `Bearer ${token}` } };

  const load = () => axios.get(`${API}/marketing/campaigns`, H).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const save = async () => {
    if (!form.name.trim()) { toast.error("Campaign name is required."); return; }
    setBusy(true);
    try {
      const payload = { ...form, cost: Number(form.cost) || 0, views: Number(form.views) || 0, reach: Number(form.reach) || 0 };
      if (form.id) await axios.patch(`${API}/marketing/campaigns/${form.id}`, payload, H);
      else await axios.post(`${API}/marketing/campaigns`, payload, H);
      toast.success(form.id ? "Campaign updated." : "Campaign created.");
      setForm(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save campaign."); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete "${c.name}"? Tracking history stays in the database.`)) return;
    await axios.delete(`${API}/marketing/campaigns/${c.id}`, H);
    toast.success("Campaign deleted.");
    load();
  };

  const copy = (text, key) => { navigator.clipboard.writeText(text); setCopied(key); setTimeout(() => setCopied(""), 1500); };

  if (!rows) return <div className="flex items-center gap-2 text-white/50 py-16 justify-center"><Loader2 className="animate-spin" size={18} /> Loading campaigns…</div>;

  const visible = kindFilter ? rows.filter((r) => r.kind === kindFilter) : rows.filter((r) => r.kind !== "qr");
  const tiktoks = visible.filter((r) => r.kind === "tiktok_video");
  const regular = visible.filter((r) => r.kind !== "tiktok_video");

  const Table = ({ list, tiktok }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-white/40 text-xs uppercase tracking-wider text-left border-b border-white/10">
            <th className="py-2 pr-3">{tiktok ? "Video" : "Campaign"}</th>
            {!tiktok && <th className="py-2 pr-3">Channel</th>}
            <th className="py-2 pr-3 text-right">Cost</th>
            <th className="py-2 pr-3 text-right">{tiktok ? "Views" : "Reach"}</th>
            <th className="py-2 pr-3 text-right">Clicks</th>
            <th className="py-2 pr-3 text-right">Leads</th>
            <th className="py-2 pr-3 text-right">Bookings</th>
            <th className="py-2 pr-3 text-right">Revenue</th>
            <th className="py-2 pr-3 text-right">$/Lead</th>
            <th className="py-2 pr-3 text-right">Profit</th>
            <th className="py-2 pr-3 text-right">ROI</th>
            <th className="py-2 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {list.map((c) => (
            <tr key={c.id} data-testid={`campaign-row-${c.slug}`} className="border-b border-white/5">
              <td className="py-2.5 pr-3">
                <div className="text-white font-semibold">{c.name}</div>
                {c.video_url ? <a href={c.video_url} target="_blank" rel="noreferrer" className="text-[#F2B705]/80 text-xs hover:underline truncate block max-w-[180px]">{c.video_url}</a>
                  : <div className="text-white/35 text-xs">?src={c.slug}</div>}
              </td>
              {!tiktok && <td className="py-2.5 pr-3"><span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70 text-xs whitespace-nowrap">{c.channel}</span></td>}
              <td className="py-2.5 pr-3 text-right text-white/70">{fmt$(c.cost)}</td>
              <td className="py-2.5 pr-3 text-right text-white/70">{Number(tiktok ? c.views : c.reach || c.views).toLocaleString()}</td>
              <td className="py-2.5 pr-3 text-right text-white/70">{c.clicks}</td>
              <td className="py-2.5 pr-3 text-right text-white/70">{c.leads}</td>
              <td className="py-2.5 pr-3 text-right text-white font-semibold">{c.bookings}</td>
              <td className="py-2.5 pr-3 text-right text-[#F2B705] font-semibold">{fmt$(c.revenue)}</td>
              <td className="py-2.5 pr-3 text-right text-white/60">{c.cost_per_lead != null ? fmt$(c.cost_per_lead) : "—"}</td>
              <td className={`py-2.5 pr-3 text-right font-semibold ${c.profit > 0 ? "text-green-400" : c.profit < 0 ? "text-red-400" : "text-white/50"}`}>{fmt$(c.profit)}</td>
              <td className={`py-2.5 pr-3 text-right ${c.roi > 0 ? "text-green-400" : c.roi < 0 ? "text-red-400" : "text-white/40"}`}>{c.roi != null ? `${c.roi}%` : "—"}</td>
              <td className="py-2.5 text-right whitespace-nowrap">
                <button data-testid={`campaign-copy-${c.slug}`} onClick={() => copy(c.url, c.id)} title="Copy tracking link" className="p-1.5 text-white/50 hover:text-white">
                  {copied === c.id ? <CheckCircle2 size={14} className="text-green-400" /> : <Copy size={14} />}
                </button>
                <button data-testid={`campaign-edit-${c.slug}`} onClick={() => setForm({ ...EMPTY, ...c, cost: c.cost || "", views: c.views || "", reach: c.reach || "" })} className="p-1.5 text-white/50 hover:text-white"><Pencil size={14} /></button>
                <button data-testid={`campaign-delete-${c.slug}`} onClick={() => remove(c)} className="p-1.5 text-white/50 hover:text-red-400"><Trash2 size={14} /></button>
              </td>
            </tr>
          ))}
          {list.length === 0 && <tr><td colSpan={12} className="py-8 text-center text-white/40">Nothing here yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );

  return (
    <div data-testid="campaign-manager" className="space-y-5">
      <div className="flex items-center gap-3">
        <h3 className="font-display font-bold text-white mr-auto">{title || "Campaign Manager"}</h3>
        <button data-testid="campaign-new-btn" onClick={() => setForm({ ...EMPTY, kind: kindFilter || "campaign" })}
          className="flex items-center gap-1.5 px-4 py-2 rounded-md bg-[#F2B705] text-black font-bold text-sm"><Plus size={15} /> New {kindFilter === "qr" ? "QR Code" : "Campaign"}</button>
      </div>

      {form && (
        <div className="bg-[#0C0C0C] border border-[#F2B705]/30 rounded-xl p-5 grid md:grid-cols-3 gap-4">
          <div className="md:col-span-3 text-white font-semibold">{form.id ? `Edit — ${form.name}` : "New Campaign"}</div>
          <label className="text-xs text-white/50 uppercase tracking-wider">Name
            <input data-testid="campaign-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Summer Sale / Nissan Altima video / QR - Gas Stations" className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm normal-case" />
          </label>
          {form.kind !== "qr" && (
            <label className="text-xs text-white/50 uppercase tracking-wider">Type
              <select data-testid="campaign-kind-select" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value, channel: e.target.value === "tiktok_video" ? "TikTok" : form.channel })} disabled={!!form.id} className="mt-1 w-full bg-black border border-white/15 rounded-md px-2 py-2 text-white text-sm normal-case">
                <option value="campaign">Ad Campaign</option>
                <option value="tiktok_video">TikTok Video</option>
              </select>
            </label>
          )}
          {form.kind === "campaign" && (
            <label className="text-xs text-white/50 uppercase tracking-wider">Channel
              <select data-testid="campaign-channel-select" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} className="mt-1 w-full bg-black border border-white/15 rounded-md px-2 py-2 text-white text-sm normal-case">
                {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </label>
          )}
          {form.kind === "tiktok_video" && (
            <label className="text-xs text-white/50 uppercase tracking-wider">Video URL
              <input data-testid="campaign-video-url" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="https://www.tiktok.com/@autohire/video/…" className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm normal-case" />
            </label>
          )}
          <label className="text-xs text-white/50 uppercase tracking-wider">Ad Spend ($)
            <input data-testid="campaign-cost-input" type="number" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} placeholder="0" className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm" />
          </label>
          <label className="text-xs text-white/50 uppercase tracking-wider">{form.kind === "tiktok_video" ? "Views" : "Views (manual)"}
            <input data-testid="campaign-views-input" type="number" value={form.views} onChange={(e) => setForm({ ...form, views: e.target.value })} placeholder="0" className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm" />
          </label>
          <label className="text-xs text-white/50 uppercase tracking-wider">Reach (manual)
            <input data-testid="campaign-reach-input" type="number" value={form.reach} onChange={(e) => setForm({ ...form, reach: e.target.value })} placeholder="0" className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm" />
          </label>
          <label className="md:col-span-2 text-xs text-white/50 uppercase tracking-wider">Notes
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional" className="mt-1 w-full bg-transparent border-b-2 border-white/15 focus:border-[#F2B705] outline-none py-2 text-white text-sm normal-case" />
          </label>
          <div className="md:col-span-3 flex gap-2">
            <button data-testid="campaign-save-btn" onClick={save} disabled={busy} className="px-5 py-2 rounded-md bg-[#F2B705] text-black font-bold text-sm disabled:opacity-50">{busy ? "Saving…" : form.id ? "Save Changes" : "Create Campaign"}</button>
            <button onClick={() => setForm(null)} className="px-5 py-2 rounded-md bg-white/5 border border-white/10 text-white/70 text-sm">Cancel</button>
          </div>
        </div>
      )}

      {!kindFilter && (
        <>
          <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5">
            <h4 className="text-white/80 font-semibold text-sm mb-3">Ad Campaigns</h4>
            <Table list={regular} />
          </div>
          <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5">
            <h4 className="text-white/80 font-semibold text-sm mb-1 flex items-center gap-2"><Music2 size={15} className="text-[#F2B705]" /> TikTok Videos</h4>
            <p className="text-white/40 text-xs mb-3">Put the tracking link in your video caption/bio. Now you can say "that Altima video made me $2,400" instead of "it got 15,000 views."</p>
            <Table list={tiktoks} tiktok />
          </div>
        </>
      )}
      {kindFilter && <div className="bg-[#0C0C0C] border border-white/10 rounded-xl p-5"><Table list={visible} /></div>}
    </div>
  );
}
